import MediaImage from "@/components/ui/MediaImage";
/* eslint-disable @next/next/no-img-element */
import React from "react";
import { ImageData } from "@/cms/models";

interface AiMarkingBadgeProps {
  image?: ImageData | null;
}

/**
 * DISABLED - the marking is deliberately never shown on the website right now (US:21289, decided
 * 2026-07-31). Flip SHOW_AI_MARKING below to switch it back on; nothing else needs rewiring.
 *
 * Everything the feature needs is intentionally left in place so re-enabling is a one-line change:
 *  - MediaItemFraction.aiMarking is requested in services/queries/get-case.ts and mapped onto
 *    ImageData in mapCase, for the case gallery query only.
 *  - The official EU assets sit in public/ai-marking (LABEL_AI_black.svg is the one used).
 *  - This component is still rendered from CaseMediaViewer, in the gallery modal.
 *
 * Note the value only ever arrives as non-null if core-apps populates managed fields on
 * HomepageCaseFraction.Images. That was reverted too, so aiMarking currently reads as null
 * regardless of what is registered in b2b - both sides have to come back for this to work.
 *
 * EU AI Act Art. 50 marking for AI-generated/modified media.
 *
 * Shown ONLY inside the gallery modal (the fullscreen view behind the "Billeder" button), not on the
 * case page's gallery tiles and not on any other image surface.
 *
 * Renders nothing unless the media item is explicitly marked "Ja". A registered "Nej" (false) and a
 * never-registered value (null/undefined) both mean no icon, so the check is strict.
 *
 * The asset is the official EU label (LABEL_AI_black.svg): a square, self-contained black disc with
 * white lettering. The wrapper therefore adds no background of its own - a black square behind a
 * black circle would make the mark read as a box instead of a badge - and no theme colours are
 * involved, so a tenant cannot restyle a regulatory mark into invisibility.
 *
 * Anchored bottom-left of the image: in the modal the top-left carries the image description and the
 * top-right the close button. `max-w` caps the box so a missing asset can't blow out the layout with
 * alt text.
 */
// Typed as boolean rather than left as a literal so the code below stays reachable to the compiler.
const SHOW_AI_MARKING: boolean = false;

const AiMarkingBadge: React.FC<AiMarkingBadgeProps> = ({ image }) => {
  if (!SHOW_AI_MARKING || image?.aiMarking !== true) {
    return null;
  }

  return (
    <div className="pointer-events-none absolute bottom-0 left-0 z-10 mb-4 ml-4 flex select-none items-center">
      <MediaImage
        src="/ai-marking/LABEL_AI_black.svg"
        alt="Billedet er helt eller delvist genereret med kunstig intelligens"
        className="h-10 w-auto max-w-[96px]"
      />
    </div>
  );
};

export default AiMarkingBadge;
